import { skipToken } from "@reduxjs/toolkit/query";
import {
  IntegrationProvider,
  type ZoomContactCenterIntegration,
} from "@repo/app-types/entities/integration";
import { countRecordingImportCustomerRows } from "@repo/app-types/entities/recordingImport";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { FC } from "react";
import { Navigate, Outlet, useOutletContext } from "react-router";
import { useRecordingImportStatusCountsQuery } from "../../../api/entities/recordingImport.js";
import { useZoomContactCenterIntegration } from "../../../api/entities/zoomContactCenterIntegration.js";
import { appPaths } from "../../../app/appPaths.js";
import {
  PageBody,
  TabbedPageLayout,
} from "../../../components/layout/pageLayout.js";
import {
  RouteTabs,
  type RouteTabItem,
} from "../../../components/layout/routeTabs.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { useSettingsSectionsPolicy } from "../settingsSectionsPolicy.js";
import { IntegrationLogo } from "./integrationLogo.js";

const ZOOM_TITLE = "Zoom Contact Center";

/**
 * What the detail page's tab(s) share: the Zoom CC integration and its load
 * state. Fetched once by the section shell and handed down through the router
 * `Outlet` so a tab doesn't re-subscribe.
 */
export interface ZoomContactCenterDetailOutletContext {
  integration: ZoomContactCenterIntegration | null;
  isLoading: boolean;
  isError: boolean;
  onReload: () => void;
}

export function useZoomContactCenterDetailOutletContext(): ZoomContactCenterDetailOutletContext {
  return useOutletContext<ZoomContactCenterDetailOutletContext>();
}

/**
 * The Zoom Contact Center integration detail page: a wider, tabbed frame (like
 * Genesys) hosting the **Settings** tab (the connection UI) and, once
 * connected, the **Failed imports** tab (the integration's action-required and failed
 * `recording_import` rows, with reprocess and skip). The other integrations keep the
 * narrow, non-tabbed detail rendered by `IntegrationSettingsSection`; only
 * Zoom CC routes here, via its own literal `zoom-contact-center` route.
 */
export const ZoomContactCenterIntegrationDetailSection: FC = () => {
  const isZoomEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.ZoomCcIntegration,
  );
  const { usesRecruitingIntegrations } = useSettingsSectionsPolicy();

  // Recruiting companies get the recruiting integrations page and never have
  // Zoom CC; gate the fetch (and redirect below) the same way the shared
  // section's visibility does.
  const isAvailable = isZoomEnabled && !usesRecruitingIntegrations;
  const zoomStatus = useZoomContactCenterIntegration(isAvailable);
  const integration = zoomStatus.currentData?.integration ?? null;
  const importCounts = useRecordingImportStatusCountsQuery(
    integration ? { integrationId: integration.id } : skipToken,
  );

  if (!isAvailable) {
    return <Navigate to={appPaths.settingsIntegrations.build()} replace />;
  }

  const importIssueCount = importCounts.data
    ? countRecordingImportCustomerRows(importCounts.data)
    : 0;
  const tabs: RouteTabItem[] = [
    { label: "Settings", to: "." },
    ...(integration
      ? [
          {
            label:
              importIssueCount > 0
                ? `Failed imports (${importIssueCount.toLocaleString()})`
                : "Failed imports",
            to: "failed-imports",
          },
        ]
      : []),
  ];

  const context: ZoomContactCenterDetailOutletContext = {
    integration,
    isLoading: !zoomStatus.currentData && !zoomStatus.isError,
    isError: zoomStatus.isError && !zoomStatus.currentData,
    onReload: () => void zoomStatus.refetch(),
  };

  return (
    <TabbedPageLayout
      header={
        <PageHeader
          width={PageWidth.Wide}
          title={
            <span className="flex min-w-0 items-center gap-2">
              <IntegrationLogo
                provider={IntegrationProvider.ZOOM_CONTACT_CENTER}
              />
              <span className="truncate">{ZOOM_TITLE}</span>
            </span>
          }
          tabs={
            <RouteTabs ariaLabel="Zoom Contact Center sections" tabs={tabs} />
          }
        />
      }
    >
      <PageBody width={PageWidth.Wide}>
        <Outlet context={context} />
      </PageBody>
    </TabbedPageLayout>
  );
};
